import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators';
import chatApi, { messageListener, ChatMessage } from '@/service/chatApi';
import Vue from 'vue';
import '@/service/chatApi';

const archiveTime = new Date();
archiveTime.setDate(archiveTime.getDate() - 60);

export interface Chat {
    sessionId: string;
    clientId: string;
    clientName: string;
    messages: Array<{
        type: "text";
        author: string;
        isRead: boolean;
        data: {
            text: string;
        }
    }>;
    wasClosed?: boolean;
    creationTime: Date;
    lastMessageTime?: Date;
}

let registeredMessageListeners: Array<messageListener> = [];
let waitingSessions: string[] = [];

@Module({
    name: 'chat'
})
export default class ChatModule extends VuexModule {
    public connected: boolean = false;
    public chats: Array<Chat> = [];

    public currentChat: Chat|null = null;

    @Mutation
    public setCurrentChat(chat: Chat|null) {
        if (chat) {
            this.chats
                .find(c => c.clientId === chat.clientId && c.sessionId === chat.sessionId)!
                .wasClosed = false;
        } else if (this.currentChat) {
            this.chats
                .find(c => c.clientId === this.currentChat!.clientId && c.sessionId === this.currentChat!.sessionId)!
                .wasClosed = true;
        }

        this.currentChat = chat;
    }

    @Mutation
    public setConnected(connected: boolean) {
        this.connected = connected;
    }

    @Mutation
    public addChats(chats: Chat[]) {
        const existingChats = chats.filter(
            c => this.chats.some(ec => ec.clientId === c.clientId && ec.sessionId === c.sessionId)
        );
        const nonExistingChats = chats.filter(
            c => !this.chats.some(ec => ec.clientId === c.clientId && ec.sessionId === c.sessionId)
        );

        nonExistingChats.forEach(chat => this.chats.push(chat));
        existingChats.forEach(ec => {
            const index = this.chats.findIndex(c => c.clientId === ec.clientId && c.sessionId === ec.sessionId);
            Vue.set(this.chats, index, ec);
        });
    }

    @Mutation
    public addMessage(message: { author: string; clientId: string; sessionId: string; text: string; isRead: boolean }) {
        const chatIndex = this.chats.findIndex(chat => chat.sessionId === message.sessionId && chat.clientId === message.clientId);
        
        if (chatIndex !== -1) {
            const chat = this.chats[chatIndex];
            chat.messages.push({ 
                type: "text", 
                author: message.author, 
                isRead: message.isRead,
                data: { text: message.text } 
            });
            Vue.set(this.chats, chatIndex, chat);
        }
    }

    @Mutation
    public setChatMessagesRead({ sessionId, clientId }: { sessionId: string; clientId: string; }) {
        const setRead = () => {
            const chatIndex = this.chats.findIndex(c => c.sessionId === sessionId && c.clientId === clientId);
            if (chatIndex === -1) {
                throw new Error('Chat not found');
            }
            const chat = this.chats[chatIndex];

            chat.messages = chat.messages.map(msg => ({ ...msg, isRead: true }));
            Vue.set(this.chats, chatIndex, chat);
            chatApi.setChatRead(chat.sessionId, chat.clientId);
        }
        
        if (document.visibilityState === 'visible') {
            setRead();
        } else {
            document.addEventListener('visibilitychange', setRead, { once: true });
        }
    }

    @Mutation
    public removeSessionChats(sessionId: string) {
        this.chats = this.chats.filter(({ sessionId: id }) => id !== sessionId);
    }

    @Action({
        rawError: true,
        commit: 'setCurrentChat'
    })
    public openChat(chatToOpen: Chat) {
        const chat = this.chats.find(c => c.clientId === chatToOpen.clientId && c.sessionId === chatToOpen.sessionId);

        if (
            chat &&
            chat.messages.filter(m => !m.isRead && m.author !== 'me').length > 0
        ) {
            this.setChatMessagesRead(chat);
        }

        return chat;
    }

    @Action({
        rawError: true
    })
    public closeChat() {
        this.setCurrentChat(null);
    }

    @Action({
        rawError: true,
        commit: 'addMessage'
    })
    public async sendMessage(message: ChatMessage) {
        chatApi.sendMessage(message);

        return { ...message, isRead: false, author: "me" };
    }

    @Action({
        rawError: true
    })
    public receiveMessage(message: ChatMessage & { isRead: boolean }) {
        const chat = this.chats.find(c => c.clientId === message.clientId && c.sessionId === message.sessionId);

        this.addMessage({ ...message, author: chat!.clientName });

        if (this.currentChat === chat) {
            this.setChatMessagesRead(chat);
        } else if (chat && !this.currentChat && !chat.wasClosed) {
            this.openChat(chat);
        }
    }

    @Action({
        rawError: true,
        commit: 'setConnected'
    })
    public async connectChat() {
        console.log('connectChat');
        const chatListener = (message: ChatMessage) => this.receiveMessage({ ...message, isRead: false });
        registeredMessageListeners.push(chatListener);
        await chatApi.connect();
        chatApi.registerMessageListener(chatListener);
        chatApi.registerChatListListener((chatList) => {
            this.context.commit('addChats', chatList.filter(
                chat => !this.chats.some(c => c.clientId === chat.sessionId && c.sessionId === chat.sessionId)
            ).map(chat => ({
                ...chat,
                messages: chat.messages.map(msg => ({
                    type: "text",
                    author: msg.senderRole === 'PHOTOGRAPHER' ? 'me' : chat.clientName,
                    data: {
                        text: msg.text
                    },
                    isRead: msg.isRead
                }))
            })));
        });
        chatApi.registerChatCreatedListener((chat) => {
            this.addChats([ { ...chat, messages: [] } ]);
        })

        if (waitingSessions.length > 0) {
            chatApi.requestChats(waitingSessions);
        }

        return true;
    }

    @Action({
        rawError: true,
        commit: 'setConnected'
    })
    public disconnectChat() {
        console.log('disconnectChat');
        chatApi.disconnect();

        return false;
    }

    @Action({
        rawError: true
    })
    public requestSessionChats(sessionIds: string[]) {
        sessionIds = sessionIds.filter(
            id => !this.chats.some(chat => chat.sessionId === id)
        );

        if (sessionIds.length === 0) {
            return;
        }

        if (this.connected) {
            chatApi.requestChats(sessionIds);
        } else {
            waitingSessions = [ ...waitingSessions, ...sessionIds ];
        }
    }

    @Action({
        rawError: true
    })
    public deleteSession(sessionId: string) {
        this.removeSessionChats(sessionId);
    }

    get currentChats() {
        return this.chats
            .filter(({ creationTime, lastMessageTime }) => 
              !creationTime || 
              creationTime > archiveTime ||
              (lastMessageTime && lastMessageTime > archiveTime)
            );
    }

    get archivedChats() {
        return this.chats
            .filter(({ creationTime, lastMessageTime }) => 
              (creationTime < archiveTime && (!lastMessageTime || lastMessageTime < archiveTime))
            );
    }
}
